import { initializeApp } from 'firebase/app';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAuth, initializeAuth, indexedDBLocalPersistence } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

// Long-polling auto-detect kills the ~30s WebChannel stall on mobile carriers;
// the persistent cache lets reads paint instantly from disk. If IndexedDB is
// unavailable the SDK degrades to memory cache internally — this init is
// synchronous and never throws for that case.
//
// BUG FIX (navigation/live-updates follow-up, ask #3 — "second window blank"):
// this used to be persistentSingleTabManager(), on the reasoning that the
// multi-tab manager's IndexedDB lease-locking protocol leaves an orphaned
// lease (and a ~30s wait for it to expire) when iOS Safari kills a
// backgrounded tab without running its cleanup. That trade-off turned out to
// be worse in practice for the much more common case this broke: opening a
// SECOND desktop tab/window hung on a blank screen for 17+ seconds and then
// misrouted, because persistentSingleTabManager() REFUSES to share its lease
// with a second tab at all (it's not "each tab gets its own independent
// snapshot," as the old comment assumed — a second tab's persistence layer
// never acquires ownership and every read against it stalls). This app's own
// name for itself is literally about working across more than one window at
// once, so persistentMultipleTabManager() — Firestore's actual supported
// mechanism for several tabs sharing one cache via a coordinated lease
// hand-off — is the correct default now; its protocol is specifically
// designed to recover from a tab disappearing without clean shutdown
// (renewal-based, not a single all-or-nothing lock), which should handle the
// iOS Safari case being guarded against far better than silently breaking
// multi-tab did. Every call site downstream (onSnapshot listeners, getDoc
// reads) is unchanged — this is purely which tab-coordination protocol the
// persistent cache uses underneath them.
//
// Main app db — persistent IndexedDB cache for the logged-in app (offline/speed).
// Wrapped in try/catch: on a storage-blocked device (private mode, Android WebView,
// blocked/full IndexedDB) persistence init can fail; instead of white-screening the
// whole app we fall back to a default in-memory Firestore instance — the same
// "never block on persistence" safety net this already had, now also the
// explicit fallback if the multi-tab manager itself ever fails to initialize.
let _db;
try {
  _db = initializeFirestore(app, {
    experimentalAutoDetectLongPolling: true,
    localCache: persistentLocalCache({
      tabManager:    persistentMultipleTabManager(),
      cacheSizeBytes: 40 * 1024 * 1024, // 40 MB — explicit cap; default is also 40 MB but
                                         // naming it here makes eviction behaviour visible
    }),
  });
} catch (err) {
  console.warn('Persistent Firestore cache unavailable — falling back to memory cache:', err);
  _db = getFirestore(app);
}
export const db = _db;

// Public/anonymous pages (PhotoReportPage, SigningPage, other /*-report/:token pages)
// use a SEPARATE Firebase app with a memory-only Firestore — NO persistentLocalCache,
// so it never touches IndexedDB and a customer's browser storage state (private mode,
// WebView, blocked storage) can never break these public pages. A second app is
// required because a single app can't hold two Firestore instances with different
// caches. No localCache option => the SDK's default in-memory cache.
const publicApp = initializeApp(firebaseConfig, 'public');
export const publicDb = initializeFirestore(publicApp, {
  experimentalAutoDetectLongPolling: true, // keep the mobile long-poll fix for customers
});

export const functions = getFunctions(app);
// Native (Capacitor WebView): browserLocalPersistence/browserSessionPersistence
// are IndexedDB-backed on web but silently fall back to in-memory persistence
// in a WebView that doesn't share Safari/Chrome's storage partition — every
// Login.js "Remember me" choice would otherwise sign the user out on every
// cold app launch. indexedDBLocalPersistence works correctly inside a
// WebView, so native always gets it regardless of "Remember me" (hidden on
// native in Login.js — there's no session-only option there). Web behavior
// (the rememberMe-driven setPersistence call in Login.js) is unchanged.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);
export const storage   = getStorage(app);
export default app;
